/**
 * Wiki Article Reader — read-only view of a knowledge base article.
 * Shows rendered HTML content with metadata sidebar.
 *
 * Closes #689 — adds Download (Markdown export via Blob), Previous and
 * Next neighbour-article navigation. Disabled at list edges so the
 * controls never navigate to undefined ids.
 *
 * Library LB6-T2 (L6-16 / L6-17). Previous / Next are the SERVER's answer —
 * GET /knowledge/documents/:id/neighbours, under the same visibility rule,
 * folder and order as the list the article was opened from (its folder and
 * order ride in the URL; opened from anywhere else, every article this
 * person may see, most recently changed first). They used to be computed
 * from a 100-row GET /wiki/articles in the browser, so past the 100th
 * article they were silently disabled. A failed read of the article is an
 * error with a retry; only a 404 says "Article not found".
 *
 * Library LB6-T9 (decision 39 / C3, L6-12). History opens the article's
 * version history (components/kb/ArticleHistoryPanel.tsx — the one place the
 * web reads versions): list them, read one, restore one with an in-app
 * confirmation. A restore re-indexes the article; the reader then re-reads it,
 * so the restored text and the new version number show here at once. The
 * article's text is drawn by components/kb/ArticleBody.tsx, the same component
 * History draws a saved version with (review LB6-T9-REV-01).
 *
 * Download uses a client-side Blob — no new server endpoint, air-gap
 * compatible, matches the established pattern for PR #826's document
 * row download.
 */
import { useState } from 'react';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import {
  ArticleStateSchema,
  DOCUMENT_SEARCH_MAX_LENGTH,
  DocumentListSortSchema,
  DocumentSortDirectionSchema,
  type ArticleTagShape,
} from '@argusai/shared';
import {
  ChevronLeft, ChevronRight, Download, PenSquare, Clock, Eye, User,
  Loader2, BookOpen, History,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { StarButton } from '@/components/library/star-button';
// LB6d-T1: the ONE read-only display of a document's tags (files and articles alike).
import { DocumentTagsValue } from '@/components/library/document-tags-value';
import { ArticleHistoryPanel } from '@/components/kb/ArticleHistoryPanel';
import { ArticleBody } from '@/components/kb/ArticleBody';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { apiClient } from '@/lib/api-client';
import { toast } from 'sonner';
import { useHasPermission } from '@/lib/use-has-permission';
import { useClassificationPolicy } from '@/lib/use-classification-policy';
import { articleMarkdownFile } from '@/lib/markdown-utils';
import { getApiErrorMessage } from '@/lib/api-error';
import { useDocumentNeighbours, wikiArticlesQueryKey, type LibraryDocumentsQuery } from '@/lib/use-library-documents';

/**
 * The list the article was opened from (L6-16): its folder (an id or 'root'),
 * its text search, its article state and its order — the SAME question the
 * list asked, so Previous / Next stay inside what the person was looking at
 * (LB6T2-BUG-04 / REV-03: the search and the state used to be dropped). All
 * optional — absent, the reader's Previous / Next walk every article this
 * person may see, most recently changed first.
 */
export interface ArticleReaderSearch {
  folderId?: string;
  /** The list's Tag filter (decision 60, LB6c-T1): a tag id, or absent. */
  tagId?: string;
  articleState?: LibraryDocumentsQuery['articleState'];
  search?: string;
  sort?: LibraryDocumentsQuery['sort'];
  sortDir?: LibraryDocumentsQuery['sortDir'];
}

const UUID_SHAPE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function validateArticleReaderSearch(search: Record<string, unknown>): ArticleReaderSearch {
  const folderId = typeof search.folderId === 'string' && search.folderId.length > 0 ? search.folderId : undefined;
  // A tag id is uuid-shaped or absent — the server refuses anything else.
  const tagId = typeof search.tagId === 'string' && UUID_SHAPE.test(search.tagId) ? search.tagId : undefined;
  const articleState = ArticleStateSchema.safeParse(search.articleState);
  const text =
    typeof search.search === 'string' ? search.search.trim().slice(0, DOCUMENT_SEARCH_MAX_LENGTH) : '';
  const sort = DocumentListSortSchema.safeParse(search.sort);
  const sortDir = DocumentSortDirectionSchema.safeParse(search.sortDir);
  return {
    folderId,
    tagId,
    articleState: articleState.success ? articleState.data : undefined,
    search: text.length > 0 ? text : undefined,
    sort: sort.success ? sort.data : undefined,
    sortDir: sortDir.success ? sortDir.data : undefined,
  };
}

/** The neighbours' question: articles only, in the list's folder, search, state and order. */
export function readerNeighbourQuery(search: ArticleReaderSearch): LibraryDocumentsQuery {
  return {
    kind: 'article',
    folderId: search.folderId,
    tagId: search.tagId,
    articleState: search.articleState,
    search: search.search,
    sort: search.sort ?? 'updatedAt',
    sortDir: search.sortDir ?? 'desc',
  };
}

export const Route = createFileRoute('/library/article/$articleId')({
  validateSearch: validateArticleReaderSearch,
  component: ArticleReaderPage,
});

interface Article {
  id: string; title: string; content: string; contentText: string; excerpt: string | null;
  status: string; version: number;
  authorName: string; viewCount: number;
  // The one tag wire shape GET /wiki/articles/:id serves (ArticleTagSchema).
  tags: ArticleTagShape[];
  sensitivity: string; exportControl: string;
  createdAt: string; updatedAt: string; publishedAt: string | null;
}

/**
 * Edit — opens the WikiEditor on THIS article (LIB-03: the article id rides
 * the search param; documents.tsx handles ?edit=<articleId> on mount). The
 * editor saves through PUT /wiki/articles/:id, which declares write:wiki
 * (L6-02), so the button asks exactly that (L6-04). It asked
 * write:library.documents before, so a Member opened an editor whose Save
 * was refused with a 403. Exported for the per-role control test.
 */
export function ArticleEditButton({ articleId }: { articleId: string }) {
  const canEditArticle = useHasPermission('write', 'wiki');
  if (!canEditArticle) return null;
  return (
    <Link to="/library/documents" search={{ edit: articleId }}>
      <Button size="sm">
        <PenSquare className="h-4 w-4 mr-1" />Edit
      </Button>
    </Link>
  );
}

/** Did the read answer 404 — the article does not exist or is not this person's to see? */
function isNotFound(err: unknown): boolean {
  return (err as { status?: number } | null)?.status === 404;
}

function ArticleReaderPage() {
  const { articleId } = Route.useParams();
  const search = Route.useSearch();
  const navigate = useNavigate();
  // Decision 28 (B7, LB6-T7) — an article is a document: its classification
  // badge shows only while a framework shows the sensitivity control.
  const { controls: classificationControls } = useClassificationPolicy();

  const articleQ = useQuery<Article, Error>({
    queryKey: [...wikiArticlesQueryKey, 'detail', articleId] as const,
    queryFn: () => apiClient.get<Article>(`/wiki/articles/${encodeURIComponent(articleId)}`),
    retry: false,
    staleTime: 0,
  });

  // #689 — Previous / Next: the server's neighbours in the list this article
  // was opened from (L6-16), never a page of a list searched in the browser.
  const neighboursQ = useDocumentNeighbours(articleId, readerNeighbourQuery(search));
  const prevArticle = neighboursQ.data?.prev ?? null;
  const nextArticle = neighboursQ.data?.next ?? null;
  const neighboursFailed = neighboursQ.isError;
  const goTo = (id: string) => navigate({ to: '/library/article/$articleId', params: { articleId: id }, search });
  // Decision 39 (C3, LB6-T9): the History panel — list the versions, read
  // one, restore one. Every reader may open it (the version reads are
  // read:library); its Restore asks write:wiki itself.
  const [showHistory, setShowHistory] = useState(false);

  if (articleQ.isLoading) return <div className="flex justify-center py-24"><Loader2 className="h-8 w-8 animate-spin text-muted-foreground" /></div>;
  // A failed read is an error; only a 404 is "not found" (L6-17).
  if (articleQ.isError && !isNotFound(articleQ.error)) {
    return (
      <div className="p-6">
        <Link to="/library/documents"><Button variant="ghost"><ChevronLeft className="h-4 w-4 mr-1" />Back</Button></Link>
        <div role="alert" data-testid="article-read-error" className="text-center text-destructive py-16 space-y-2">
          <p>The article could not be loaded: {getApiErrorMessage(articleQ.error, 'the request failed')}</p>
          <Button variant="outline" size="sm" onClick={() => void articleQ.refetch()}>Retry</Button>
        </div>
      </div>
    );
  }
  const article = articleQ.data;
  if (!article) return <div className="p-6"><Link to="/library/documents"><Button variant="ghost"><ChevronLeft className="h-4 w-4 mr-1" />Back</Button></Link><p className="text-center text-destructive py-16">Article not found</p></div>;

  const handleDownload = () => {
    // #689 — Markdown export via client-side Blob. The reader stores
    // rendered HTML on the server (Tiptap → HTML at save); we round-trip
    // through the one HTML→Markdown converter (lib/markdown-utils.ts — the
    // editor's Preview MD and export use it too; decision 43 deleted this
    // file's private copy). The title is the file's one `# ` heading and the
    // body's headings sit a level under it, as before. Air-gap safe: no
    // remote converter.
    const slug = (article.title || 'article').replace(/[^a-z0-9-_]+/gi, '-').toLowerCase();
    const blob = new Blob([articleMarkdownFile(article.title, article.content)], { type: 'text/markdown;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${slug}.md`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success('Downloaded');
  };

  return (
    <div className="p-6 space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Link to="/library/documents"><Button variant="ghost" size="icon" className="h-8 w-8" aria-label="Back to documents" title="Back"><ChevronLeft className="h-5 w-5" /></Button></Link>
          <div>
            <div className="flex items-center gap-1">
              <h1 className="text-xl font-bold">{article.title}</h1>
              {/* THE personal star — an article is a document (decision 19), starred as one (decision 38). */}
              <StarButton item={{ id: article.id, kind: 'document' }} size="md" />
            </div>
            <p className="text-sm text-muted-foreground">by {article.authorName} · {new Date(article.updatedAt).toLocaleDateString()}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {/* #689 — Previous / Next neighbour navigation. Disabled at
              list edges so we never navigate to an undefined id. */}
          <Button
            variant="outline"
            size="icon"
            className="h-8 w-8"
            aria-label="Previous article"
            title={prevArticle ? `Previous: ${prevArticle.title}` : neighboursFailed ? 'The neighbouring articles could not be loaded' : 'Previous article'}
            disabled={!prevArticle}
            onClick={() => {
              if (prevArticle) goTo(prevArticle.id);
            }}
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <Button
            variant="outline"
            size="icon"
            className="h-8 w-8"
            aria-label="Next article"
            title={nextArticle ? `Next: ${nextArticle.title}` : neighboursFailed ? 'The neighbouring articles could not be loaded' : 'Next article'}
            disabled={!nextArticle}
            onClick={() => {
              if (nextArticle) goTo(nextArticle.id);
            }}
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
          <Button
            variant="outline"
            size="sm"
            aria-label="Download article as Markdown"
            title="Download as Markdown"
            onClick={handleDownload}
          >
            <Download className="h-4 w-4 mr-1" />Download
          </Button>
          <Button
            variant={showHistory ? 'secondary' : 'outline'}
            size="sm"
            aria-label="Version history"
            aria-expanded={showHistory}
            title="Version history"
            onClick={() => setShowHistory((open) => !open)}
          >
            <History className="h-4 w-4 mr-1" />History
          </Button>
          <ArticleEditButton articleId={articleId} />
        </div>
      </div>

      {showHistory ? (
        <ArticleHistoryPanel
          key={articleId}
          articleId={articleId}
          current={{ title: article.title, content: article.content, excerpt: article.excerpt }}
          onClose={() => setShowHistory(false)}
        />
      ) : null}

      {/* Two column: content + metadata */}
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_280px] gap-4">
        {/* Article content */}
        <Card>
          <CardContent className="pt-6">
            <ArticleBody html={article.content} />
          </CardContent>
        </Card>

        {/* Metadata sidebar */}
        <div className="space-y-4">
          <Card>
            <CardHeader className="pb-3"><CardTitle className="text-sm flex items-center gap-1"><BookOpen className="h-3.5 w-3.5" />Publishing</CardTitle></CardHeader>
            <CardContent className="space-y-3 text-sm">
              <div className="flex justify-between"><span className="text-muted-foreground">Status</span><Badge variant={article.status === 'published' ? 'default' : 'secondary'} className="capitalize">{article.status}</Badge></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Version</span><span className="font-medium">v{article.version}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground flex items-center gap-1"><Eye className="h-3 w-3" />Views</span><span className="font-medium">{article.viewCount}</span></div>
              {article.publishedAt && <div className="flex justify-between"><span className="text-muted-foreground">Published</span><span className="font-medium">{new Date(article.publishedAt).toLocaleDateString()}</span></div>}
            </CardContent>
          </Card>

          <DocumentTagsValue tags={article.tags} />

          <Card>
            <CardHeader className="pb-3"><CardTitle className="text-sm">Metadata</CardTitle></CardHeader>
            <CardContent className="space-y-3 text-sm">
              <div className="flex justify-between"><span className="text-muted-foreground flex items-center gap-1"><User className="h-3 w-3" />Author</span><span className="font-medium">{article.authorName}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground flex items-center gap-1"><Clock className="h-3 w-3" />Created</span><span className="font-medium">{new Date(article.createdAt).toLocaleDateString()}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground flex items-center gap-1"><Clock className="h-3 w-3" />Updated</span><span className="font-medium">{new Date(article.updatedAt).toLocaleDateString()}</span></div>
              {classificationControls.sensitivity ? (
                <div className="flex justify-between"><span className="text-muted-foreground">Classification</span><Badge variant="outline" className="capitalize text-xs">{article.sensitivity ?? 'unclassified'}</Badge></div>
              ) : null}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}

// Reference Separator so the existing import stays load-bearing for
// the type-checker even though the reader hasn't grown a horizontal
// rule yet. Removing the import alters the diff surface unnecessarily.
void Separator;
